<template>
  <div
    class="text-center ml-4 text-gray-500"
    :class="paddingLeftClass"
  >
    <div>
      <div class="h-12 flex items-center justify-center text-base font-bold heading text-gray-800">
        {{ title }}
      </div>

      <div class="py-2">
        <div
          v-if="selectedTab === 'Summary'"
          class="flex items-center justify-center"
        >
          <div>
            {{ formatCurrency(getTotalCosts()) }}
          </div>
        </div>

        <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
          {{ formatCurrency(getPartnershipFee()) }}
        </div>

        <div
          v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown"
          style="margin-top: 2px"
        >
          {{ formatCurrency(0) }}
        </div>

        <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
          {{ formatCurrency(pData.AvgProjectedCardFees) }}
        </div>

        <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
          {{ formatCurrency(totalAvgPayroll(pData)) }}
        </div>

        <div
          v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown"
          style="margin-top: 2px"
        >
          {{ formatCurrency(pData.AvgProjectedCustServCost) }}
        </div>
        <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown">
          {{ formatCurrency(pData.AvgProjectedRenewalCost) }}
        </div>
        <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown">
          {{ formatCurrency(pData.AvgProjectedSalesOnboardingCost) }}
        </div>

        <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
          {{ formatCurrency(pData.MarketingCost) }}
        </div>
        <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
          {{ formatCurrency(pData.SocialCost) }}
        </div>

        <div
          v-if="selectedTab === 'Profit'"
          :class="getTextColor(pData.ProjectedProfit.Max)"
        >
          {{ formatCurrency(pData.ProjectedProfit.Max) }}
        </div>
        <div
          v-if="selectedTab === 'ROI'"
          :class="getTextColor(pData.ProjectedROI.Max * 100)"
        >
          {{ formatPercentage(pData.ProjectedROI.Max * 100) }}
        </div>

        <div
          v-if="selectedTab === 'Revenue'"
          class="flex items-center justify-center"
        >
          {{ formatCurrency(pData.NewMemberRevenue) }}
        </div>
        <div v-if="selectedTab === 'Revenue' && openNewMemberGrowthBreakdown">
          {{ safe(pData.NewMembers) }}
        </div>
        <div v-if="selectedTab === 'Revenue' && openNewMemberGrowthBreakdown">
          {{ formatCurrency(pData.NewMemberRevenue) }}
        </div>
      </div>

      <div class="py-2">
        <div
          v-if="selectedTab === 'Summary'"
          class="flex items-center justify-center"
        >
          <div>
            {{ formatCurrency(pData.AvgProjectedNYTotalRevenue) }}
          </div>
        </div>

        <div
          v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown"
          style="margin-top: 2px"
        >
          {{ formatCurrency(pData.AvgProjectedNYRetention) }}
        </div>
        <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
          {{ formatCurrency(pData.AvgProjectedNYGrowth) }}
        </div>
        <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
          {{ formatCurrency(pData.AvgProjectedNYAwayRevenue) }}
        </div>

        <div
          v-if="selectedTab === 'Profit'"
          class="text-xl font-semibold text-gray-800"
          :class="getTextColor(pData.ProjectedProfit.Mean)"
        >
          {{ formatCurrency(pData.ProjectedProfit.Mean) }}
        </div>
        <div
          v-if="selectedTab === 'ROI'"
          class="text-xl font-semibold text-gray-800"
          :class="getTextColor(pData.ProjectedROI.Mean * 100)"
        >
          {{ formatPercentage(pData.ProjectedROI.Mean * 100) }}
        </div>

        <div
          v-if="selectedTab === 'Revenue'"
          class="flex items-center justify-center"
        >
          {{ formatCurrency(pData.RetainedMemberRevenue) }}
        </div>
        <div v-if="selectedTab === 'Revenue' && openMemberRetentionBreakdown">
          {{ safe(pData.RetainedMembers) }}
        </div>
        <div v-if="selectedTab === 'Revenue' && openMemberRetentionBreakdown">
          {{ formatCurrency(pData.RetainedMemberRevenue) }}
        </div>
      </div>

      <div class="py-2">
        <div
          v-if="selectedTab === 'Summary'"
          :class="getTextColor(pData.ProjectedProfit.Mean)"
        >
          {{ formatCurrency(pData.ProjectedProfit.Mean) }}
        </div>

        <div v-if="selectedTab === 'Costs'">
          {{ formatCurrency(getPartnershipFee()) }}
        </div>

        <div
          v-if="selectedTab === 'Profit'"
          :class="getTextColor(pData.ProjectedProfit.Min)"
        >
          {{ formatCurrency(pData.ProjectedProfit.Min) }}
        </div>

        <div
          v-if="selectedTab === 'ROI'"
          :class="getTextColor(pData.ProjectedROI.Min * 100)"
        >
          {{ formatPercentage(pData.ProjectedROI.Min * 100) }}
        </div>

        <div
          v-if="selectedTab === 'Revenue'"
          class="flex items-center justify-center"
        >
          {{ formatCurrency(revTotal(pData)) }}
        </div>
        <div v-if="selectedTab === 'Revenue' && openTotalHomeClubMembersBreakdown">
          {{ membersTotal(pData) }}
        </div>
        <div v-if="selectedTab === 'Revenue' && openTotalHomeClubMembersBreakdown">
          {{ formatCurrency(revTotal(pData)) }}
        </div>

        <div
          v-if="selectedTab === 'Revenue'"
          class="py-2 font-normal"
        >
          <div class="flex items-center justify-center">
            {{ formatCurrency(pData.AvgProjectedNYAwayRevenue) }}
          </div>
        </div>
      </div>

      <div
        v-if="selectedTab === 'Profit' && (pData.ProjectedProfit.RiskOfLoss || 0) > 0"
        class="py-2 text-red-500"
      >
        <div>
          {{ formatPercentage(pData.ProjectedProfit.RiskOfLoss) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'YearColumn',
    props: {
      title: { type: String, required: true },
      comparisonResults: { type: Object, required: true },
      productIndex: { type: Number, required: true },
      selectedTab: { type: String, required: true },

      openAvgTotalCostBreakdown: { type: Boolean, default: false },
      openPayrollBreakdown: { type: Boolean, default: false },
      openAvgTotalRevenueBreakdown: { type: Boolean, default: false },
      openNewMemberGrowthBreakdown: { type: Boolean, default: false },
      openMemberRetentionBreakdown: { type: Boolean, default: false },
      openTotalHomeClubMembersBreakdown: { type: Boolean, default: false },

      paddingLeftClass: { type: String, default: 'pl-20' },
      years: { type: Number, default: 2 },
    },
    computed: {
      pData() {
        const p
          = this.comparisonResults && this.comparisonResults.Product2
            ? this.comparisonResults.Product2[this.productIndex]
            : {};
        return p || {};
      },
    },
    methods: {
      getPartnershipFee() {
        if (this.years === 2) {
          return 1750;
        }
        if (this.years === 3) {
          if (this.productIndex === 0) return 600; // Year 1
          if (this.productIndex === 1) return 1575; // Year 2
          if (this.productIndex === 2) return 1575; // Year 3
        }
        return this.pData.Fee || 0;
      },
      getTotalCosts() {
        if (!this.pData) return 0;

        return (
          this.getPartnershipFee()
          + (this.pData.AvgProjectedCardFees || 0)
          + (this.pData.AvgProjectedCustServCost || 0)
          + (this.pData.AvgProjectedRenewalCost || 0)
          + (this.pData.AvgProjectedSalesOnboardingCost || 0)
          + (this.pData.MarketingCost || 0)
          + (this.pData.SocialCost || 0)
        );
      },
      totalAvgPayroll(p) {
        if (!p) return 0;
        const {
          AvgProjectedCustServCost = 0,
          AvgProjectedRenewalCost = 0,
          AvgProjectedSalesOnboardingCost = 0,
        } = p;
        return AvgProjectedCustServCost + AvgProjectedRenewalCost + AvgProjectedSalesOnboardingCost;
      },
      revTotal(p) {
        if (!p) return 0;
        const { NewMemberRevenue = 0, RetainedMemberRevenue = 0 } = p;
        return NewMemberRevenue + RetainedMemberRevenue;
      },
      membersTotal(p) {
        if (!p) return 0;
        const { NewMembers = 0, RetainedMembers = 0 } = p;
        return NewMembers + RetainedMembers;
      },
      safe(v) {
        return Number.isFinite(v) ? v : v || 0;
      },

      formatCurrency(value) {
        const v = Number(value || 0);
        return v
          .toLocaleString('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
          .split('.')[0];
      },
      getTextColor(value) {
        const v = Number(value || 0);
        return Math.sign(v) === -1 ? 'text-red-500' : '';
      },
      formatPercentage(value) {
        const v = Number(value || 0);
        return `${v.toFixed(2)}%`;
      },
    },
  };
</script>
